import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  MessageCircle, 
  Send, 
  X, 
  Bot, 
  User,
  FileText,
  Loader2,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

export default function DocumentChat({ 
  documentId, 
  documentName, 
  isGlobal = false,
  open,
  onOpenChange
}) {
  const { api } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [indexStatus, setIndexStatus] = useState({ indexed: false, chunks: 0 });
  const [indexing, setIndexing] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && !isGlobal && documentId) {
      checkIndexStatus();
    }
  }, [open, documentId, isGlobal]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const checkIndexStatus = async () => {
    try {
      const response = await api().get(`/documents/${documentId}/index-status`);
      setIndexStatus(response.data);
    } catch (error) {
      console.error('Failed to check index status');
    }
  };

  const handleIndex = async () => {
    setIndexing(true);
    try {
      if (isGlobal) {
        await api().post('/documents/index-all');
        toast.success('All documents indexed for RAG');
      } else {
        await api().post(`/documents/${documentId}/index`);
        toast.success('Document indexed successfully');
        await checkIndexStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Indexing failed');
    } finally {
      setIndexing(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const endpoint = isGlobal 
        ? '/documents/global-chat' 
        : `/documents/${documentId}/chat`;
      
      const response = await api().post(endpoint, {
        message: input,
        chat_history: messages.slice(-6)
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        sources: response.data.sources
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get response');
      // Remove the user message if error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[80vh] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-primary" />
            {isGlobal ? 'Chat with All Documents' : `Chat with ${documentName}`}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Index Status Banner */}
          {!isGlobal && !indexStatus.indexed && (
            <div className="mx-4 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-amber-800">Document not indexed</p>
                  <p className="text-sm text-amber-600">Index this document to enable AI chat</p>
                </div>
                <Button onClick={handleIndex} disabled={indexing} size="sm">
                  {indexing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Index Document
                </Button>
              </div>
            </div>
          )}

          {isGlobal && (
            <div className="mx-4 mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-blue-800">Global Document Chat</p>
                  <p className="text-sm text-blue-600">Search across all indexed documents</p>
                </div>
                <Button onClick={handleIndex} disabled={indexing} variant="outline" size="sm">
                  {indexing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Re-index All
                </Button>
              </div>
            </div>
          )}

          {/* Messages */}
          <ScrollArea className="flex-1 px-4 py-4" ref={scrollRef}>
            {messages.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Bot className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Ask a question about {isGlobal ? 'your documents' : 'this document'}</p>
                <p className="text-sm mt-1">
                  {isGlobal 
                    ? "I'll search across all indexed documents to find answers" 
                    : "I'll use the document content to provide accurate answers"}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg, idx) => (
                  <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                    {msg.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Bot className="h-4 w-4 text-primary" />
                      </div>
                    )}
                    <div className={`max-w-[80%] ${msg.role === 'user' ? 'order-first' : ''}`}>
                      <div className={`rounded-lg px-4 py-2 ${
                        msg.role === 'user' 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-muted'
                      }`}>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      </div>
                      {msg.sources && msg.sources.length > 0 && (
                        <div className="mt-2 space-y-1">
                          <p className="text-xs text-muted-foreground">Sources:</p>
                          {msg.sources.map((src, i) => (
                            <div key={i} className="text-xs bg-muted/50 rounded p-2">
                              <div className="flex items-center gap-2 mb-1">
                                <FileText className="h-3 w-3" />
                                <span className="font-medium">
                                  {src.document || 'This document'}
                                </span>
                                <Badge variant="outline" className="text-[10px]">
                                  Page {src.page}
                                </Badge>
                                <Badge variant="secondary" className="text-[10px]">
                                  {(src.score * 100).toFixed(0)}% match
                                </Badge>
                              </div>
                              <p className="text-muted-foreground line-clamp-2">{src.text}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {msg.role === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 text-primary-foreground" />
                      </div>
                    )}
                  </div>
                ))}
                {loading && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                    <div className="bg-muted rounded-lg px-4 py-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>

          {/* Input */}
          <div className="p-4 border-t">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={`Ask about ${isGlobal ? 'your documents' : 'this document'}...`}
                disabled={loading || (!isGlobal && !indexStatus.indexed)}
                className="flex-1"
              />
              <Button 
                onClick={handleSend} 
                disabled={loading || !input.trim() || (!isGlobal && !indexStatus.indexed)}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
